interface GameItem {
  id: string;
  name: string;
  icon: string;
  category: string;
  status: number;
  activeUsers: number;
  retention1Day: number;
  retention7Day: number;
  adImpressions: number;
  adRevenue: number;
  updatedAt: string;
}

interface RecommendedGame {
  gameId: string;
  order: number;
  selectedAt: string;
}

interface ChannelGameData {
  onlineGames: GameItem[];
  allGames: GameItem[];
  recommendedGames: RecommendedGame[];
  lastUpdated: {
    onlineGames: string;
    allGames: string;
    recommendedGames: string;
  };
}

const STORAGE_KEY = 'channelGameData';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30分钟过期

export const channelGameCache = {
  migrateCache: (): void => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return;

      const parsedData: { [key: string]: any } = JSON.parse(data);
      let migrated = false;

      Object.keys(parsedData).forEach(channelId => {
        const channelData = parsedData[channelId];
        
        // 删除 ChannelGameData 中的冗余字段
        if (channelData.channelId !== undefined) {
          delete channelData.channelId;
          migrated = true;
        }
        
        if (channelData.channelName !== undefined) {
          delete channelData.channelName;
          migrated = true;
        }
        
        // 清理 GameItem 中的冗余字段
        ['onlineGames', 'allGames'].forEach(gameType => {
          if (channelData[gameType] && Array.isArray(channelData[gameType])) {
            channelData[gameType] = channelData[gameType].map((game: any) => {
              const { channelId, channelName, ...rest } = game;
              return rest;
            });
            migrated = true;
          }
        });
      });

      if (migrated) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsedData));
        console.log('✅ 缓存迁移完成：已删除冗余字段');
      }
    } catch (error) {
      console.error('❌ 缓存迁移失败:', error);
    }
  },
  getChannelData: (channelId: string): ChannelGameData | null => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return null;

      const parsedData: { [key: string]: ChannelGameData } = JSON.parse(data);
      const channelData = parsedData[channelId];

      if (!channelData) return null;

      return channelData;
    } catch (error) {
      console.error('获取缓存数据失败:', error);
      return null;
    }
  },

  setChannelData: (channelId: string, data: Partial<ChannelGameData>): void => {
    try {
      const existingData = localStorage.getItem(STORAGE_KEY);
      const parsedData: { [key: string]: ChannelGameData } = existingData ? JSON.parse(existingData) : {};
      
      const currentData = parsedData[channelId] || {
        onlineGames: [],
        allGames: [],
        recommendedGames: [],
        lastUpdated: {
          onlineGames: '',
          allGames: '',
          recommendedGames: ''
        }
      };

      const updatedData = {
        ...currentData,
        ...data,
        lastUpdated: {
          ...currentData.lastUpdated,
          ...data.lastUpdated
        }
      };

      parsedData[channelId] = updatedData;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error('保存缓存数据失败:', error);
    }
  },

  isCacheExpired: (channelId: string, dataType: 'onlineGames' | 'allGames' | 'recommendedGames'): boolean => {
    const channelData = channelGameCache.getChannelData(channelId);
    if (!channelData) return true;

    const lastUpdated = channelData.lastUpdated[dataType];
    if (!lastUpdated) return true;

    const now = new Date().getTime();
    const cacheTime = new Date(lastUpdated).getTime();

    return now - cacheTime > CACHE_EXPIRY;
  },

  updateOnlineGames: (channelId: string, games: GameItem[]): void => {
    const channelData = channelGameCache.getChannelData(channelId);
    if (!channelData) return;

    channelGameCache.setChannelData(channelId, {
      onlineGames: games,
      lastUpdated: {
        ...channelData.lastUpdated,
        onlineGames: new Date().toISOString()
      }
    });
  },

  updateAllGames: (channelId: string, games: GameItem[]): void => {
    const channelData = channelGameCache.getChannelData(channelId);
    if (!channelData) return;

    channelGameCache.setChannelData(channelId, {
      allGames: games,
      lastUpdated: {
        ...channelData.lastUpdated,
        allGames: new Date().toISOString()
      }
    });
  },

  updateRecommendedGames: (channelId: string, games: RecommendedGame[]): void => {
    const channelData = channelGameCache.getChannelData(channelId);
    if (!channelData) return;

    channelGameCache.setChannelData(channelId, {
      recommendedGames: games,
      lastUpdated: {
        ...channelData.lastUpdated,
        recommendedGames: new Date().toISOString()
      }
    });
  },

  clearChannelData: (channelId: string): void => {
    try {
      const existingData = localStorage.getItem(STORAGE_KEY);
      if (!existingData) return;

      const parsedData: { [key: string]: ChannelGameData } = JSON.parse(existingData);
      delete parsedData[channelId];

      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error('清除缓存数据失败:', error);
    }
  }
};

export type { GameItem, RecommendedGame, ChannelGameData };